import React from 'react';
import { Stepper } from '../../../stepper/stepper';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { CountedTextarea } from '../../../counted-textarea/counted-textarea';
import { StatusPill } from '../../../status-pill/status-pill';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../../../form-kit';
import { useMediaQuery } from '../../../hooks/use-media-query';
import { down } from '../../../../style-utils/media-queries';
import type { StaySource } from '../../../../interfaces/stays';
import { EMAIL_MAX, FieldErrors, FormKey, NAME_MAX, NOTES_MAX, PHONE_MAX, SOURCE_CHOICES, StayForm } from './reservation-form-model';
import { FormSection, LabelRow, SectionTitle } from './reservation-form.styled';

/**
 * THE GUEST AND NOTES SECTIONS of the stay form (plan S4, S4.9) — shared by
 * the New stay page and the Edit stay modal. The parent owns the state; these
 * draw fields and report edits.
 */

/** What every section of the form is handed. */
export interface FieldKit {
  form: StayForm;
  errors: FieldErrors;
  disabled: boolean;
  update: (patch: Partial<StayForm>) => void;
  /** A field lost focus (money fields check themselves then). */
  blur?: (key: FormKey) => void;
  /** The element id of a field. */
  id: (key: string) => string;
  /** Fields the person changed before a stale reload: each wears a tag. */
  changed?: ReadonlySet<string>;
  /** Flat sections (inside a modal) instead of cards (on a page). */
  flat?: boolean;
}

export const errorId = (kit: FieldKit, key: string): string => `${kit.id(key)}-error`;
export const hintId = (kit: FieldKit, key: string): string => `${kit.id(key)}-hint`;

/** aria-describedby for a field: its error when it has one, else its hint. */
export const describe = (kit: FieldKit, key: string, hint = false): string | undefined =>
  kit.errors[key] ? errorId(kit, key) : hint ? hintId(kit, key) : undefined;

/** A field label, with the "changed" tag when the field was carried over a reload. */
export const FieldLabel: React.FC<{ kit: FieldKit; field: string; htmlFor?: string; id?: string; children: React.ReactNode }> = ({
  kit,
  field,
  htmlFor,
  id,
  children,
}) => (
  <LabelRow>
    <Label htmlFor={htmlFor} id={id}>
      {children}
    </Label>
    {kit.changed && kit.changed.has(field) ? (
      <StatusPill tone="brand" data-changed={field}>
        Changed
      </StatusPill>
    ) : null}
  </LabelRow>
);

/* The radio pill from sm up; a native select below (five options do not fit
   a 320px row). False without matchMedia, so tests get the pill. */
const NARROW = down('sm').replace(/^@media\s*/, '');

export const GuestFields: React.FC<{ kit: FieldKit; maxGuests: number; headingId: string }> = ({ kit, maxGuests, headingId }) => {
  const { form, errors, disabled, update, id } = kit;
  const narrow = useMediaQuery(NARROW);
  const max = Math.max(1, maxGuests || 1);

  const text = (key: 'lead_first_name' | 'lead_last_name' | 'lead_email' | 'lead_phone') => ({
    id: id(key),
    value: form[key],
    disabled,
    autoComplete: 'off',
    'aria-invalid': errors[key] ? true : undefined,
    hasError: !!errors[key],
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => update({ [key]: event.target.value } as Partial<StayForm>),
  });

  return (
    <FormSection $flat={kit.flat} aria-labelledby={headingId}>
      <SectionTitle id={headingId}>Guest</SectionTitle>
      <FieldRow>
        <Field>
          <FieldLabel kit={kit} field="lead_first_name" htmlFor={id('lead_first_name')}>
            First name
          </FieldLabel>
          <Input {...text('lead_first_name')} maxLength={NAME_MAX} aria-describedby={describe(kit, 'lead_first_name')} />
          <FieldError id={errorId(kit, 'lead_first_name')} message={errors.lead_first_name} />
        </Field>
        <Field>
          <FieldLabel kit={kit} field="lead_last_name" htmlFor={id('lead_last_name')}>
            Last name
          </FieldLabel>
          <Input {...text('lead_last_name')} maxLength={NAME_MAX} aria-describedby={describe(kit, 'lead_last_name')} />
          <FieldError id={errorId(kit, 'lead_last_name')} message={errors.lead_last_name} />
        </Field>
      </FieldRow>

      <FieldRow>
        <Field id={id('party_size')}>
          <FieldLabel kit={kit} field="party_size">
            Guests
          </FieldLabel>
          <Stepper
            value={Math.min(Math.max(1, form.party_size), max)}
            min={1}
            max={max}
            label="guests"
            disabled={disabled}
            onChange={value => update({ party_size: value })}
          />
          <FieldError id={errorId(kit, 'party_size')} message={errors.party_size} />
        </Field>
      </FieldRow>

      {/* Its own row: five choices squeezed into half a modal's width broke
          their labels across lines. */}
      <Field>
        {narrow ? (
          <>
            <FieldLabel kit={kit} field="source" htmlFor={id('source')}>
              Booked through
            </FieldLabel>
            <Select
              id={id('source')}
              value={form.source}
              disabled={disabled}
              aria-invalid={errors.source ? true : undefined}
              aria-describedby={describe(kit, 'source')}
              onChange={event => update({ source: event.target.value as StaySource })}
            >
              {SOURCE_CHOICES.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </>
        ) : (
          <div id={id('source')}>
            <FieldLabel kit={kit} field="source" id={`${id('source')}-label`}>
              Booked through
            </FieldLabel>
            <SegmentedControl
              options={SOURCE_CHOICES}
              value={form.source}
              label="Booked through"
              disabled={disabled}
              onChange={value => update({ source: value })}
            />
          </div>
        )}
        <FieldError id={errorId(kit, 'source')} message={errors.source} />
      </Field>

      <FieldRow>
        <Field>
          <FieldLabel kit={kit} field="lead_phone" htmlFor={id('lead_phone')}>
            Phone (optional)
          </FieldLabel>
          <Input
            {...text('lead_phone')}
            type="tel"
            inputMode="tel"
            maxLength={PHONE_MAX}
            aria-describedby={describe(kit, 'lead_phone')}
          />
          <FieldError id={errorId(kit, 'lead_phone')} message={errors.lead_phone} />
        </Field>
        <Field>
          <FieldLabel kit={kit} field="lead_email" htmlFor={id('lead_email')}>
            Email (optional)
          </FieldLabel>
          <Input
            {...text('lead_email')}
            type="email"
            inputMode="email"
            maxLength={EMAIL_MAX}
            aria-describedby={describe(kit, 'lead_email', true)}
          />
          <FieldHint id={hintId(kit, 'lead_email')}>Used to send the check-in link</FieldHint>
          <FieldError id={errorId(kit, 'lead_email')} message={errors.lead_email} />
        </Field>
      </FieldRow>
    </FormSection>
  );
};

export const NotesFields: React.FC<{ kit: FieldKit; headingId: string }> = ({ kit, headingId }) => {
  const { form, errors, disabled, update, id } = kit;

  return (
    <FormSection $flat={kit.flat} aria-labelledby={headingId}>
      <SectionTitle id={headingId}>Notes</SectionTitle>
      <Field>
        <FieldLabel kit={kit} field="notes" htmlFor={id('notes')}>
          Private notes
        </FieldLabel>
        <CountedTextarea
          id={id('notes')}
          value={form.notes}
          max={NOTES_MAX}
          rows={3}
          disabled={disabled}
          hasError={!!errors.notes}
          aria-describedby={errors.notes ? errorId(kit, 'notes') : undefined}
          onChange={event => update({ notes: event.target.value })}
        />
        <FieldError id={errorId(kit, 'notes')} message={errors.notes} />
      </Field>
    </FormSection>
  );
};
